import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismCard,
  PrismLoadingBar,
  PrismMetricBlock,
} from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import HotelOverviewDashboardUpdateIndicator from 'lumen/components/hotel/overview-dashboard/update-indicator';
import formatOdRateValue from 'lumen/helpers/format-od-rate-value';
import getNumberSignClass from 'lumen/helpers/get-number-sign-class';
import highlightElement from 'lumen/modifiers/highlight-element';
import { OD_API_DATA_KEYS } from 'lumen/utils/constants/overview-dashboard-constants';
import { KPIS } from 'lumen/utils/constants/ri-constant-values';
import formatLastUpdatedTimestamp from 'lumen/utils/overview-dashboard/format-last-updated-timestamp';
import generateRevenueInsightRedirectUrlQueryParams from 'lumen/utils/overview-dashboard/generate-revenue-insight-redirect-url-query-params';

interface HotelOverviewDashboardTilesTopInsightsMyAdrSignature {
  Args: {
    data: unknown;
    isLoading: unknown;
    selectedRange: unknown;
    selectedRefDaysAgo: unknown;
    tileId: unknown;
  };
}

export default class HotelOverviewDashboardTilesTopInsightsMyAdr extends Component<HotelOverviewDashboardTilesTopInsightsMyAdrSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare odSelections: Services['od-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get adrRefDiffValue() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.data.getDataValueFromKey(
      this.odSelections,
      OD_API_DATA_KEYS.adr_diff,
      // @ts-expect-error: Incorrect type
      this.args.selectedRefDaysAgo.id,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get adrValue() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.data.getDataValueFromKey(
      this.odSelections,
      OD_API_DATA_KEYS.adr,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRefDiffValue() {
    let result = this.intl.t('general.missing');

    if (this.adrRefDiffValue !== null) {
      if (this.adrRefDiffValue === 0) {
        result = this.intl.t('overview_dashboard.kpi.no_change');
      } else {
        // @ts-expect-error: Incorrect type
        result = this.hotelAndMarketService.formatRate(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          this.adrRefDiffValue,
          true,
          true,
          { maximumFractionDigits: 0, minimumFractionDigits: 0 },
          true,
          true,
        );
      }
    }

    return result;
  }

  // The redirect link redirects the user to Revenue insight (reflects selected period or active day drilled down)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiRedirectUrlQueryParams() {
    // @ts-expect-error: Incorrect type
    if (this.odSelections.isDayDrilldownActive) {
      const activeDrilldownDayMoment = dayjs(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.odSelections.activeDrilldownDayAsString,
      );

      return generateRevenueInsightRedirectUrlQueryParams(
        KPIS.ADR,
        activeDrilldownDayMoment,
        activeDrilldownDayMoment,
      );
    }

    return generateRevenueInsightRedirectUrlQueryParams(
      KPIS.ADR,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.args.selectedRange.start,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.args.selectedRange.end,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get updateIndicatorContent() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const isOutOfSync = this.args.data.isDataOutOfSync(OD_API_DATA_KEYS.adr);
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const lastUpdatedTimestamp = this.args.data.getDataLastUpdateTimestamp(
      OD_API_DATA_KEYS.adr,
    );

    if (lastUpdatedTimestamp) {
      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        isOutOfSync,
        formattedLastUpdatedTimestamp: formatLastUpdatedTimestamp(
          this.intl,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          lastUpdatedTimestamp,
        ),
      };
    }

    return null;
  }

  <template>
    <PrismCard
      class="prism-card--od-kpi"
      {{! @glint-expect-error: Incorrect type }}
      {{highlightElement id=@tileId isLoading=@isLoading}}
    >
      <:header as |Header|>
        <Header.title>
          {{t "overview_dashboard.kpi.my_adr"}}
        </Header.title>
      </:header>

      <:headerAfter>
        {{#if (and (not @isLoading) this.updateIndicatorContent)}}
          <HotelOverviewDashboardUpdateIndicator
            @updateIndicatorContent={{this.updateIndicatorContent}}
          />
        {{/if}}
      </:headerAfter>

      <:content>
        <div class="od-kpi-content flex flex-col justify-end">
          {{#if @isLoading}}
            <PrismLoadingBar />
          {{else}}
            <PrismMetricBlock @align="left">
              <:value>
                <span class="text-400--bold">
                  {{formatOdRateValue this.adrValue true}}
                </span>
              </:value>
              <:label>
                <span class={{getNumberSignClass this.adrRefDiffValue}}>
                  {{this.formattedRefDiffValue}}
                </span>
              </:label>
            </PrismMetricBlock>
            <div class="mt-400">
              <LinkTo
                @model={{this.hotelAndMarketService.hotel.id}}
                @query={{this.kpiRedirectUrlQueryParams}}
                @route="hotel.revenue-insight"
                class="prism-link prism-link--standalone"
                data-event-id="hotel_overview-kpi_main_redirect-click"
                data-event-value={{@tileId}}
                target="_blank"
              >
                {{t "overview_dashboard.kpi.link.revenue"}}
              </LinkTo>
            </div>
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
